import React from 'react';
import { ErrorMessage, Field } from 'formik';
import { Divider, FormControl, FormLabel } from '@material-ui/core';
import get from 'lodash/get';
import { produce } from 'immer';
import InputErrorMessage from '../../InputErrorMessage';
import DatePicker from '../../DatePicker';
import UploadRxFormFile from '../../../pages/RxForms/components/UploadRxFormFile';
import {
  ARCHES,
  ASSIGNMENTS,
  EXPORTS,
  IDB_ATTRIBUTES,
  QUESTIONS,
  ROOT_INTEGRATION_TYPES,
  SCANNERS,
  VIEW_LABEL
} from './constants';
import {
  autoDetectStlTypes,
  getDefaultPreferenceTrayDesignAssignment,
  validateDicomUpload
} from './helpers';
import AssignmentField from './Fields/AssignmentField';
import RadioGroupField from '../Fields/RadioGroupField';
import ScanSubmissionMethodField from './Fields/ScanSubmissionMethodField';
import InsertBracketsAssignmentField from './Fields/InsertBracketsAssignmentField';
import LocationField from '../Fields/LocationField';
import ShippingMethodField from '../Fields/ShippingMethodField';
import RootIntegrationField from './Fields/RootIntegrationField';
import ScannerField from './Fields/ScannerField';
import ExportTypeField from './Fields/ExportTypeField';
import RushCaseField from './Fields/RushCaseField';
import BracketLibrariesField from './Fields/BracketLibrariesField';
import DoctorSelectionField from '../Fields/DoctorSelectionField';
import BondingDateField from './Fields/BondingDateField';
import ArchWiresField from './Fields/ArchWiresField';
import {
  ACCEPTED_FILE_TYPES,
  FILE_TYPE_IDS,
  UPLOAD_NOTES,
  ZIP_EXTRACTABLE_FILE_TYPES
} from '../../../constants';
import TrayDesignAssignmentField from './Fields/TrayDesignAssignmentField';
import TextField from '../Fields/Formik/TextField';
import NameField from '../../Formik/NameField';
import Teeth from './Teeth';
import IntraoralTypeSwitch from './Fields/IntraoralTypeSwitch';
import tags from '../../../constants/tags';
import SanitizeForbiddenCharactersField from '../Fields/Formik/SanitizeForbiddenCharactersField';

const createIdbNativeFields = ({
  withDoctorSelection,
  doctors,
  preference,
  thirdPartyPartnerName,
  isSubmitting,
  decodedValues,
  pricing,
  saveForLaterEnabled,
  values,
  isManagement,
  setFieldValue,
  setFieldTouched,
  organization,
  rxForm,
  initialValues,
  availableLocations,
  GridField
}) => {
  const enableShippingMethods =
    decodedValues[IDB_ATTRIBUTES.PRINT_ASSIGNMENT] !== ASSIGNMENTS.IN_OFFICE;

  const existingFieldsMap = {
    [IDB_ATTRIBUTES.FIRST_NAME]: (
      <GridField xs={12} sm={6} key={IDB_ATTRIBUTES.FIRST_NAME} name={IDB_ATTRIBUTES.FIRST_NAME}>
        <Field
          variant="outlined"
          fullWidth
          required
          id="first_name"
          label="Patient First Name"
          name="first_name"
          component={SanitizeForbiddenCharactersField}
          originalComponent={NameField}
        />
      </GridField>
    ),

    [IDB_ATTRIBUTES.LAST_NAME]: (
      <GridField key={IDB_ATTRIBUTES.LAST_NAME} name={IDB_ATTRIBUTES.LAST_NAME} xs={12} sm={6}>
        <Field
          name="last_name"
          variant="outlined"
          required
          fullWidth
          id="last_name"
          label="Patient Last Name"
          component={SanitizeForbiddenCharactersField}
          originalComponent={NameField}
        />
      </GridField>
    ),

    [IDB_ATTRIBUTES.CASE_NUMBER_TEXT]: (
      <GridField
        key={IDB_ATTRIBUTES.CASE_NUMBER_TEXT}
        name={IDB_ATTRIBUTES.CASE_NUMBER_TEXT}
        xs={12}
        sm={6}
      >
        <Field
          name="case_number_text"
          variant="outlined"
          fullWidth
          id="case_number_text"
          label="Patient Case Number"
          component={SanitizeForbiddenCharactersField}
          originalComponent={TextField}
        />
      </GridField>
    ),

    [IDB_ATTRIBUTES.INTRAORAL_SCAN_DATE]: (
      <GridField
        key={IDB_ATTRIBUTES.INTRAORAL_SCAN_DATE}
        name={IDB_ATTRIBUTES.INTRAORAL_SCAN_DATE}
        xs={12}
        sm={6}
      >
        <DatePicker label="Intraoral Scan Date" name="intraoral_scan_date" required />
      </GridField>
    ),

    [IDB_ATTRIBUTES.DOCTOR]: withDoctorSelection && doctors.list.length > 1 && (
      <GridField key={IDB_ATTRIBUTES.DOCTOR} name={IDB_ATTRIBUTES.DOCTOR} xs={12} md={6}>
        <DoctorSelectionField
          name={IDB_ATTRIBUTES.DOCTOR}
          doctors={doctors.list}
          fetching={doctors.fetching}
          preferenceValue={preference[IDB_ATTRIBUTES.DOCTOR]}
          isSubmitting={isSubmitting}
        />
      </GridField>
    ),

    [IDB_ATTRIBUTES.TREATMENT_LOCATION]: (
      <GridField
        key={IDB_ATTRIBUTES.TREATMENT_LOCATION}
        name={IDB_ATTRIBUTES.TREATMENT_LOCATION}
        xs={12}
        sm={withDoctorSelection ? 6 : 12}
      >
        <LocationField
          dependency
          name={IDB_ATTRIBUTES.TREATMENT_LOCATION}
          locations={availableLocations}
          isSubmitting={isSubmitting}
          required
        />
      </GridField>
    ),

    [IDB_ATTRIBUTES.INITIAL_CASE_SETUP_ASSIGNMENT]: (
      <GridField
        key={IDB_ATTRIBUTES.INITIAL_CASE_SETUP_ASSIGNMENT}
        name={IDB_ATTRIBUTES.INITIAL_CASE_SETUP_ASSIGNMENT}
        md={12}
      >
        <>
          <AssignmentField
            name={IDB_ATTRIBUTES.INITIAL_CASE_SETUP_ASSIGNMENT}
            options={preference.initial_case_setup_assignment_options}
            thirdPartyPartnerName={thirdPartyPartnerName}
            isSubmitting={isSubmitting}
          />
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.RUSH_CASE]: (
      <GridField
        key={IDB_ATTRIBUTES.RUSH_CASE}
        name={IDB_ATTRIBUTES.RUSH_CASE}
        xs={12}
        show={decodedValues[IDB_ATTRIBUTES.INITIAL_CASE_SETUP_ASSIGNMENT] !== ASSIGNMENTS.IN_OFFICE}
      >
        <>
          <RushCaseField
            name={IDB_ATTRIBUTES.RUSH_CASE}
            preferenceValue={preference[IDB_ATTRIBUTES.RUSH_CASE]}
            dependency={
              decodedValues[IDB_ATTRIBUTES.INITIAL_CASE_SETUP_ASSIGNMENT] !== ASSIGNMENTS.IN_OFFICE
            }
            resetValue={setField => {
              setField(IDB_ATTRIBUTES.RUSH_CASE, '0');
              setField(IDB_ATTRIBUTES.RUSH_CASE_AUTHORIZED_BY_NAME, '');
            }}
            isSubmitting={isSubmitting}
            rushCaseFee={
              get(pricing, 'arch.rush_case', 0) * (decodedValues.jaws_id === ARCHES.BOTH ? 2 : 1)
            }
          />
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.SCAN_SUBMISSION_METHOD]: (
      <>
        <GridField
          key={IDB_ATTRIBUTES.SCAN_SUBMISSION_METHOD}
          name={IDB_ATTRIBUTES.SCAN_SUBMISSION_METHOD}
          xs={12}
        >
          <>
            <FormControl component="fieldset">
              <ScanSubmissionMethodField
                preferenceValue={preference.scan_submission_options}
                thirdPartyPartnerName={thirdPartyPartnerName}
                isSubmitting={isSubmitting}
              />
            </FormControl>
            <Divider />
          </>
        </GridField>

        <GridField
          name={IDB_ATTRIBUTES.SCAN_SUBMISSION_METHOD}
          xs={12}
          show={decodedValues.scan_submission_type === ASSIGNMENTS.ORTHOSELECT}
        >
          <>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">
                Upload intraoral STL files{' '}
                {saveForLaterEnabled && values[IDB_ATTRIBUTES.SAVED_FOR_LATER] ? '' : '*'}
              </FormLabel>
              <UploadRxFormFile
                fileModifier={
                  +decodedValues[IDB_ATTRIBUTES.ROOT_INTEGRATION_TYPE] ===
                    ROOT_INTEGRATION_TYPES.DICOM && isManagement
                    ? IntraoralTypeSwitch
                    : null
                }
                accept={ACCEPTED_FILE_TYPES[FILE_TYPE_IDS.stl]}
                dependency={decodedValues.scan_submission_type === ASSIGNMENTS.ORTHOSELECT}
                name={IDB_ATTRIBUTES.STL_FILES}
                preferenceValue={[]}
                title="Upload STL Files"
                onChange={(updatedFiles, filePath) => {
                  let files = autoDetectStlTypes(updatedFiles || []);

                  if (typeof filePath !== 'undefined') {
                    files = produce(files, draft => {
                      const index = draft.findIndex(file => file.file_path === filePath);

                      if (index !== -1) {
                        const updatedFile = draft[index];
                        // set other type to opposite
                        const otherIndex = draft.findIndex(
                          file => file.file_path !== filePath && file.tag
                        );

                        if (otherIndex !== -1 && draft[otherIndex].tag === updatedFile.tag) {
                          draft[otherIndex].tag =
                            updatedFile.tag === tags.STL_MANDIBULAR
                              ? tags.STL_MAXILLARY
                              : tags.STL_MANDIBULAR;
                        }
                      }
                    });
                  }

                  setTimeout(() => setFieldValue('stl_files', files));
                  setFieldTouched('stl_files', true, false);
                }}
                values={values.stl_files}
                label="Add Files"
                maxFiles={10}
                organization={organization}
                autoExtractCompressedFiles={ZIP_EXTRACTABLE_FILE_TYPES[FILE_TYPE_IDS.stl] || null}
                existingFiles={values.stl_files}
                preventDuplicate
              />
              <ErrorMessage
                name="stl_files"
                render={message => <InputErrorMessage>{message}</InputErrorMessage>}
              />
            </FormControl>
            <Divider />
          </>
        </GridField>

        <GridField
          name={IDB_ATTRIBUTES.SCANNER}
          xs={12}
          skipOnly
          show={decodedValues[IDB_ATTRIBUTES.SCAN_SUBMISSION_METHOD] === 2}
        >
          <>
            <ScannerField
              name={IDB_ATTRIBUTES.SCANNER}
              dependency={decodedValues[IDB_ATTRIBUTES.SCAN_SUBMISSION_METHOD] === 2}
              preferenceValue={SCANNERS.ITERO}
              isSubmitting={isSubmitting}
            />
            <Divider />
          </>
        </GridField>
      </>
    ),

    [IDB_ATTRIBUTES.INTRAORAL_PHOTOS]: (
      <GridField
        key={IDB_ATTRIBUTES.INTRAORAL_PHOTOS}
        name={IDB_ATTRIBUTES.INTRAORAL_PHOTOS}
        xs={12}
      >
        <>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">
              Upload intraoral photos here (we require at least one intraoral to verify the scans
              match the patient){' '}
              {saveForLaterEnabled && values[IDB_ATTRIBUTES.SAVED_FOR_LATER] ? '' : '*'}
            </FormLabel>
            <UploadRxFormFile
              accept={ACCEPTED_FILE_TYPES[FILE_TYPE_IDS.photos]}
              name={IDB_ATTRIBUTES.INTRAORAL_PHOTOS}
              title="Upload Intraoral Photos"
              onChange={files => {
                setTimeout(() => setFieldValue('rx_intraoral_photos', files));
                setFieldTouched('rx_intraoral_photos', true, false);
              }}
              values={values.rx_intraoral_photos}
              label="Add Files"
              maxFiles={10}
              organization={organization}
              preferenceValue={[]}
              autoExtractCompressedFiles={ZIP_EXTRACTABLE_FILE_TYPES[FILE_TYPE_IDS.photos] || null}
            />
            <ErrorMessage
              name="rx_intraoral_photos"
              render={message => <InputErrorMessage>{message}</InputErrorMessage>}
            />
          </FormControl>
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.ARCH_TYPE]: (
      <GridField key={IDB_ATTRIBUTES.ARCH_TYPE} name={IDB_ATTRIBUTES.ARCH_TYPE} xs={12}>
        <>
          <RadioGroupField isSubmitting={isSubmitting} name={IDB_ATTRIBUTES.ARCH_TYPE} />
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.EXPORT_TYPE]: (
      <GridField key={IDB_ATTRIBUTES.EXPORT_TYPE} name={IDB_ATTRIBUTES.EXPORT_TYPE} xs={12}>
        <>
          <ExportTypeField
            isSubmitting={isSubmitting}
            name={IDB_ATTRIBUTES.EXPORT_TYPE}
            options={preference.export_type_options}
          />
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.PRINT_ASSIGNMENT]: (
      <GridField
        key={IDB_ATTRIBUTES.PRINT_ASSIGNMENT}
        name={IDB_ATTRIBUTES.PRINT_ASSIGNMENT}
        xs={12}
      >
        <>
          <AssignmentField
            name={IDB_ATTRIBUTES.PRINT_ASSIGNMENT}
            options={preference.print_assignment_options}
            thirdPartyPartnerName={thirdPartyPartnerName}
            isSubmitting={isSubmitting}
          />
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.TRAY_DESIGN_ASSIGNMENT]: (
      <GridField
        key={IDB_ATTRIBUTES.TRAY_DESIGN_ASSIGNMENT}
        name={IDB_ATTRIBUTES.TRAY_DESIGN_ASSIGNMENT}
        xs={12}
        show={
          decodedValues[IDB_ATTRIBUTES.PRINT_ASSIGNMENT] === ASSIGNMENTS.IN_OFFICE &&
          decodedValues[IDB_ATTRIBUTES.EXPORT_TYPE] === EXPORTS.TRAYS
        }
      >
        <>
          <TrayDesignAssignmentField
            name={IDB_ATTRIBUTES.TRAY_DESIGN_ASSIGNMENT}
            options={preference.tray_design_assignment_options}
            isSubmitting={isSubmitting}
            preferenceValue={getDefaultPreferenceTrayDesignAssignment(preference)}
            dependency={
              decodedValues[IDB_ATTRIBUTES.PRINT_ASSIGNMENT] === ASSIGNMENTS.IN_OFFICE &&
              decodedValues[IDB_ATTRIBUTES.EXPORT_TYPE] === EXPORTS.TRAYS
            }
          />
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.INSERT_BRACKETS_ASSIGNMENT]: (
      <GridField
        key={IDB_ATTRIBUTES.INSERT_BRACKETS_ASSIGNMENT}
        name={IDB_ATTRIBUTES.INSERT_BRACKETS_ASSIGNMENT}
        xs={12}
      >
        <>
          <InsertBracketsAssignmentField
            currentPrintAssignment={decodedValues.print_assignment}
            isSubmitting={isSubmitting}
            preferenceValue={preference[IDB_ATTRIBUTES.INSERT_BRACKETS_ASSIGNMENT]}
            setDefault={value => {
              setFieldValue(IDB_ATTRIBUTES.INSERT_BRACKETS_ASSIGNMENT, String(value));
            }}
            thirdPartyPartnerName={thirdPartyPartnerName}
          />
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.BRACKET_LIBRARIES]: (
      <GridField
        key={IDB_ATTRIBUTES.BRACKET_LIBRARIES}
        name={IDB_ATTRIBUTES.BRACKET_LIBRARIES}
        xs={12}
      >
        <>
          <BracketLibrariesField
            originalValue={initialValues[IDB_ATTRIBUTES.BRACKET_LIBRARIES] || []}
            organizationId={organization.id}
            withCustomBracketSets={
              organization.enable_custom_bracket_sets || !!rxForm.custom_bracket_set_id
            }
            doctorId={values.user_id}
            name={IDB_ATTRIBUTES.BRACKET_LIBRARIES}
            customBracketSetAttributeName={IDB_ATTRIBUTES.CUSTOM_BRACKET_SET_ID}
            resetValueOnFetch={!rxForm.id}
          />
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.ARCH_WIRES]: (
      <GridField key={IDB_ATTRIBUTES.ARCH_WIRES} name={IDB_ATTRIBUTES.ARCH_WIRES} xs={12}>
        <>
          <ArchWiresField
            name={IDB_ATTRIBUTES.ARCH_WIRES}
            organizationId={organization.id}
            doctorId={values.user_id}
          />
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.SHIPPING_LOCATION]: (
      <GridField
        key={IDB_ATTRIBUTES.SHIPPING_LOCATION}
        name={IDB_ATTRIBUTES.SHIPPING_LOCATION}
        xs={12}
        md={6}
        show={decodedValues[IDB_ATTRIBUTES.PRINT_ASSIGNMENT] !== ASSIGNMENTS.IN_OFFICE}
      >
        <LocationField
          name={IDB_ATTRIBUTES.SHIPPING_LOCATION}
          locations={availableLocations}
          dependency={decodedValues[IDB_ATTRIBUTES.PRINT_ASSIGNMENT] !== ASSIGNMENTS.IN_OFFICE}
          preferenceValue={preference[IDB_ATTRIBUTES.SHIPPING_LOCATION]}
          isSubmitting={isSubmitting}
          required={decodedValues[IDB_ATTRIBUTES.PRINT_ASSIGNMENT] !== ASSIGNMENTS.IN_OFFICE}
        />
      </GridField>
    ),

    [IDB_ATTRIBUTES.SHIPPING_METHOD]: (
      <GridField
        key={IDB_ATTRIBUTES.SHIPPING_METHOD}
        name={IDB_ATTRIBUTES.SHIPPING_METHOD}
        xs={12}
        md={6}
        show={enableShippingMethods}
      >
        <ShippingMethodField
          name={IDB_ATTRIBUTES.SHIPPING_METHOD}
          dependency={enableShippingMethods}
          preferenceValue={preference[IDB_ATTRIBUTES.SHIPPING_METHOD]}
          isSubmitting={isSubmitting}
          resetValue={() => {
            if (!enableShippingMethods) {
              setFieldValue(IDB_ATTRIBUTES.SHIPPING_METHOD, null);
              setFieldTouched(IDB_ATTRIBUTES.SHIPPING_METHOD);
            }
          }}
        />
      </GridField>
    ),

    [IDB_ATTRIBUTES.ROOT_INTEGRATION_TYPE]: (
      <GridField
        key={IDB_ATTRIBUTES.ROOT_INTEGRATION_TYPE}
        name={IDB_ATTRIBUTES.ROOT_INTEGRATION_TYPE}
        xs={12}
      >
        <>
          <RootIntegrationField
            name={IDB_ATTRIBUTES.ROOT_INTEGRATION_TYPE}
            dependency={preference[IDB_ATTRIBUTES.ROOT_INTEGRATION]}
            preferenceValue={ROOT_INTEGRATION_TYPES.NONE} // RX Form Preference?
            isSubmitting={isSubmitting}
            fees={pricing}
          />
          {+decodedValues[IDB_ATTRIBUTES.ROOT_INTEGRATION_TYPE] ===
            ROOT_INTEGRATION_TYPES.DICOM && (
            <FormControl component="fieldset" fullWidth>
              <UploadRxFormFile
                accept={ACCEPTED_FILE_TYPES[FILE_TYPE_IDS.dicom]}
                note={UPLOAD_NOTES[FILE_TYPE_IDS.dicom]}
                name={IDB_ATTRIBUTES.DICOM_FILES}
                title="Upload Dicom Files"
                onChange={files => {
                  setTimeout(() => setFieldValue(IDB_ATTRIBUTES.DICOM_FILES, files));
                  setFieldTouched(IDB_ATTRIBUTES.DICOM_FILES, true, false);
                }}
                values={values[IDB_ATTRIBUTES.DICOM_FILES]}
                label="Add Files"
                maxFiles={1}
                organization={organization}
                preferenceValue={[]}
                validateFile={validateDicomUpload}
              />
              <ErrorMessage
                name="dicom_files"
                render={message => <InputErrorMessage>{message}</InputErrorMessage>}
              />
            </FormControl>
          )}
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.BONDING_DATE]: (
      <GridField
        key={IDB_ATTRIBUTES.BONDING_DATE}
        name={IDB_ATTRIBUTES.BONDING_DATE}
        xs={12}
        sm={6}
        show={decodedValues[IDB_ATTRIBUTES.SHIPPING_METHOD] !== 'international_shipping'}
      >
        <>
          <BondingDateField
            label={`${VIEW_LABEL[IDB_ATTRIBUTES.BONDING_DATE]}`}
            name={IDB_ATTRIBUTES.BONDING_DATE}
            createdAt={rxForm.created_at_mt || ''}
          />
          <Divider />
        </>
      </GridField>
    ),

    [IDB_ATTRIBUTES.NOTES]: (
      <GridField key={IDB_ATTRIBUTES.NOTES} name={IDB_ATTRIBUTES.NOTES} xs={12}>
        <Field
          variant="outlined"
          fullWidth
          id="special_instructions"
          label={`(Optional) ${QUESTIONS[IDB_ATTRIBUTES.NOTES]}`}
          name="special_instructions"
          multiline
          component={TextField}
        />
      </GridField>
    ),

    [IDB_ATTRIBUTES.TEETH]: (
      <GridField key={IDB_ATTRIBUTES.TEETH} name={IDB_ATTRIBUTES.TEETH} xs={12}>
        <>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">
              <ol style={{ paddingLeft: '20px' }}>
                <li>Click any teeth that are missing or will be extracted (Blue)</li>
                <li>Click again to keep the space open (Green)</li>
              </ol>
            </FormLabel>
            <Teeth
              data={values.teeth || {}}
              onChange={teeth => {
                setFieldValue(IDB_ATTRIBUTES.TEETH, teeth);
                setFieldTouched(IDB_ATTRIBUTES.TEETH, true, false);
              }}
            />
            <ErrorMessage
              name={IDB_ATTRIBUTES.TEETH}
              render={message => <InputErrorMessage>{message}</InputErrorMessage>}
            />
          </FormControl>
          <Divider />
        </>
      </GridField>
    )
  };
  return existingFieldsMap;
};

export default createIdbNativeFields;
